<template>
    <div class="py-6 text-blue-500 border-t border-blue-100 first:border-t-0">
        <h3>
            <button
                class="flex text-left w-full justify-between font-medium text-blue-500 cursor-pointer"
                :aria-expanded="isActive ? 'true' : 'false'"
                @click="toggleActive"
            >
                {{ item.title }}

                <svg
                    v-if="isActive"
                    class="flex-shrink-0 w-6 h-6 ml-2 fill-current"
                    role="img"
                    focusable="false"
                    aria-label=""
                    viewBox="0 0 20 20"
                >
                    <path
                        d="M15.5 11h-12c-0.276 0-0.5-0.224-0.5-0.5s0.224-0.5 0.5-0.5h12c0.276 0 0.5 0.224 0.5 0.5s-0.224 0.5-0.5 0.5z"
                    ></path>
                </svg>

                <svg
                    v-else
                    class="flex-shrink-0 w-6 h-6 ml-2 fill-current"
                    viewBox="0 0 20 20"
                    role="img"
                    focusable="false"
                    aria-label=""
                >
                    <path
                        d="M15.5 10h-5.5v-5.5c0-0.276-0.224-0.5-0.5-0.5s-0.5 0.224-0.5 0.5v5.5h-5.5c-0.276 0-0.5 0.224-0.5 0.5s0.224 0.5 0.5 0.5h5.5v5.5c0 0.276 0.224 0.5 0.5 0.5s0.5-0.224 0.5-0.5v-5.5h5.5c0.276 0 0.5-0.224 0.5-0.5s-0.224-0.5-0.5-0.5z"
                    ></path>
                </svg>
            </button>
        </h3>

        <template v-if="isActive">
            <div class="mt-3" role="list">
                <span
                    class="inline-block px-1 mt-1 mr-1 text-sm font-medium border rounded"
                    role="listitem"
                >
                    {{ item['categories.lvl0'][0] }}
                </span>

                <span
                    v-for="service in item.services"
                    :key="service"
                    role="listitem"
                    class="inline-block px-1 mt-1 mr-1 text-sm font-medium border rounded"
                >
                    {{ service }}
                </span>
            </div>

            <ul class="mt-6 mb-4 text-sm">
                <li v-if="item.address" class="flex mt-5">
                    <svg
                        class="flex-none w-3 h-3 mt-1 mr-3"
                        viewBox="0 0 12 13"
                        fill="none"
                        role="img"
                        focusable="false"
                        aria-label="Address"
                    >
                        <path
                            d="M6.008 11.558a16.667 16.667 0 01-1.985-2c-1.045-1.256-1.94-2.77-1.94-4.19C2.083 3.064 3.868 1.25 6 1.25c2.125 0 3.916 1.816 3.916 4.117 0 1.61-.908 3.135-1.938 4.33a13.57 13.57 0 01-1.97 1.861z"
                            class="stroke-current"
                            stroke-width="1.5"
                        />
                        <rect x="5" y="4.25" width="2" height="2" rx="1" class="fill-current" />
                    </svg>

                    <p class="text-grey-500">
                        {{ item.address }}<br />
                        <a
                            :href="
                                item.googleMapsShareLink ||
                                `https://www.google.com/maps/search/?api=1&query=${item.address}`
                            "
                            class="text-blue-500"
                            target="_blank"
                            :aria-label="'Directions to ' + item.title"
                        >
                            Directions <sup aria-hidden="true">&nearr;</sup>
                        </a>
                    </p>
                </li>

                <li v-if="item.hours" class="flex mt-5">
                    <svg
                        class="flex-none w-3 h-3 mt-1 mr-3"
                        viewBox="0 0 12 13"
                        fill="none"
                        role="img"
                        focusable="false"
                        aria-label="Opening Hours"
                    >
                        <circle
                            cx="6"
                            cy="6.5"
                            r="5.25"
                            class="stroke-current"
                            stroke-width="1.5"
                        />
                        <path
                            d="M5.906 3.25v3.969M5.156 6.484h3.969"
                            class="stroke-current"
                            stroke-width="1.5"
                        />
                    </svg>
                    <div class="">
                        <p v-for="(group, i) in openingTimes" :key="i" class="text-grey-500">
                            {{ group }}
                        </p>

                        <p class="text-grey-500">(ACST/ACDT)</p>
                        <li v-if="item.openingHoursNote" class="flex my-4">
                            <div
                                v-if="item.openingHoursNote"
                                class="small wysiwyg wysiwyg--super-condensed text-grey-500"
                                v-html="item.openingHoursNote"
                            ></div>
                        </li>

                        <div
                            v-for="(exception, i) in exceptions"
                            :key="i"
                            class="my-2 text-grey-500"
                        >
                            <p class="font-bold">{{ exception.note }}</p>
                            <p class="">{{ exception.readable }}</p>
                        </div>
                    </div>
                </li>

                <li v-if="item.phone" class="flex mt-5">
                    <svg
                        class="flex-none w-3 h-3 mt-1 mr-3"
                        viewBox="0 0 12 13"
                        fill="none"
                        role="img"
                        focusable="false"
                        aria-label="Phone number"
                    >
                        <path
                            d="M8.754 7.857a.802.802 0 00-1.082.217C7.276 8.56 6.695 8.46 5.234 7c-1.46-1.46-1.295-1.776-.808-2.172.487-.397.343-.884.217-1.082a276.11 276.11 0 00-1.136-1.732c-.145-.216-.361-.577-.83-.505C2.334 1.563 1 2.267 1 3.764 1 5.26 2.172 7.1 3.795 8.704 5.4 10.31 7.24 11.5 8.736 11.5c1.497 0 2.2-1.335 2.255-1.677.072-.469-.289-.667-.505-.83-.217-.144-1.533-1.01-1.732-1.136z"
                            class="stroke-current"
                            stroke-width="1.5"
                            stroke-miterlimit="10"
                        />
                    </svg>

                    <p class="text-grey-500">
                        {{ item.phone }}
                    </p>
                </li>

                <li v-if="item.website && !hideWebsite" class="flex mt-5">
                    <svg
                        class="flex-none w-3 h-3 mt-1 mr-3"
                        viewBox="0 0 12 13"
                        fill="none"
                        role="img"
                        focusable="false"
                        aria-label="Website"
                    >
                        <circle
                            cx="6"
                            cy="6.5"
                            r="5.25"
                            class="stroke-current"
                            stroke-width="1.5"
                        />
                        <path
                            d="M.813 6.484h10.312M7.875 6.5c0 1.578-.281 2.972-.706 3.942-.213.486-.447.833-.669 1.047-.218.211-.386.261-.5.261-.114 0-.282-.05-.5-.26-.222-.215-.456-.562-.669-1.048-.425-.97-.706-2.364-.706-3.942 0-1.578.281-2.972.706-3.942.213-.486.447-.833.669-1.047.218-.211.386-.261.5-.261.114 0 .282.05.5.26.222.215.456.562.669 1.048.425.97.706 2.364.706 3.942z"
                            class="stroke-current"
                            stroke-width="1.5"
                        />
                    </svg>

                    <p class="text-grey-500">
                        <a :href="item.website" class="break-all">{{ item.website }}</a>
                    </p>
                </li>
                <div v-if="item.note" class="wysiwyg my-3" v-html="item.note"></div>
            </ul>
        </template>
    </div>
</template>

<script>
import { formatOpeningTimes, visibleExceptions } from './util-opening-times';

export default {
    props: {
        activeMarker: {
            type: Object,
            default: undefined
        },

        item: {
            type: Object,
            required: true
        },

        expand: {
            type: Boolean,
            required: true
        },

        expandInitialised: {
            type: Boolean,
            default: false
        },

        hideWebsite: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            active: false
        };
    },

    computed: {
        // TO DO: Clean this up..
        isActive() {
            if (this.expandInitialised && !this.activeMarker) return true;
            if (this.expand) return this.expand;
            if (this.activeMarker === undefined) return this.active;
            return this.activeMarker ? this.activeMarker.id === this.item.id : false;
        },
        openingTimes() {
            return formatOpeningTimes(this.item.hours);
        },
        exceptions() {
            return visibleExceptions(this.item.openingHoursExceptions);
        }
    },

    methods: {
        toggleActive() {
            this.active = !this.active;
            if (this.active) {
                this.$emit('set-as-active');
            }
        }
    }
};
</script>
